import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare, Send } from 'lucide-react';
import {
  type FeedAudience,
  type FeedComment,
  type FeedPost,
  addComment,
  createPost,
  listComments,
  listFeed,
  reactToPost,
} from '@/api/livefeed';
import { useDirectory } from '@/hooks/useDirectory';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';

const AUDIENCES: FeedAudience[] = ['global', 'workgroup', 'department'];
const REACTIONS = ['👍', '❤️', '🎉', '🙌', '👏'];

function timeAgo(iso: string, locale: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffSec = Math.round((then - Date.now()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const abs = Math.abs(diffSec);
  if (abs < 60) return rtf.format(Math.round(diffSec), 'second');
  if (abs < 3600) return rtf.format(Math.round(diffSec / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), 'hour');
  return rtf.format(Math.round(diffSec / 86400), 'day');
}

export function LivefeedPage() {
  const { t, i18n } = useTranslation();
  const dir = useDirectory();

  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [body, setBody] = useState('');
  const [audience, setAudience] = useState<FeedAudience>('global');
  const [posting, setPosting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    listFeed()
      .then(setPosts)
      .catch((err) => setError(err?.message ?? 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function submitPost() {
    if (!body.trim()) return;
    setPosting(true);
    try {
      await createPost({ body: body.trim(), audience });
      setBody('');
      setAudience('global');
      load();
    } catch (err) {
      setError((err as Error)?.message ?? 'Failed to post');
    } finally {
      setPosting(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <header>
        <h1 className="text-2xl font-semibold text-[var(--color-lop-text-primary)]">
          {t('livefeed.title')}
        </h1>
        <p className="text-sm text-[var(--color-lop-text-secondary)]">{t('livefeed.subtitle')}</p>
      </header>

      {/* Composer */}
      <div className="space-y-3 rounded-lg border bg-card p-4" data-testid="livefeed-composer">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={t('livefeed.composer.placeholder')}
          rows={3}
          data-testid="livefeed-composer-body"
        />
        <div className="flex items-center justify-between gap-3">
          <label className="flex items-center gap-2 text-xs text-[var(--color-lop-text-secondary)]">
            {t('livefeed.composer.audience')}
            <Select
              value={audience}
              onChange={(e) => setAudience(e.target.value as FeedAudience)}
              className="h-8 w-40"
              data-testid="livefeed-composer-audience"
            >
              {AUDIENCES.map((a) => (
                <option key={a} value={a}>
                  {t(`livefeed.audience.${a}`)}
                </option>
              ))}
            </Select>
          </label>
          <Button
            size="sm"
            onClick={submitPost}
            disabled={posting || !body.trim()}
            data-testid="livefeed-composer-submit"
          >
            <Send className="mr-1 h-4 w-4" />
            {t('livefeed.composer.post')}
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-800 bg-red-950/40 p-4 text-sm text-red-300">
          {error}
        </div>
      )}
      {loading && (
        <div className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
          {t('common.loading')}
        </div>
      )}
      {!loading && !error && posts.length === 0 && (
        <div className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">
          {t('livefeed.empty')}
        </div>
      )}

      <div className="space-y-4">
        {posts.map((p) => (
          <PostCard
            key={p.id}
            post={p}
            authorName={dir.nameFor(p.authorUserId)}
            locale={i18n.language}
            nameFor={dir.nameFor}
          />
        ))}
      </div>
    </div>
  );
}

function PostCard({
  post,
  authorName,
  locale,
  nameFor,
}: {
  post: FeedPost;
  authorName: string;
  locale: string;
  nameFor: (id: string | null | undefined) => string;
}) {
  const { t } = useTranslation();
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState<FeedComment[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [draft, setDraft] = useState('');
  const [reacted, setReacted] = useState<string | null>(null);

  function toggleComments() {
    const next = !showComments;
    setShowComments(next);
    if (next && comments.length === 0) {
      setLoadingComments(true);
      listComments(post.id)
        .then(setComments)
        .catch(() => undefined)
        .finally(() => setLoadingComments(false));
    }
  }

  async function submitComment() {
    if (!draft.trim()) return;
    await addComment(post.id, draft.trim());
    setDraft('');
    const fresh = await listComments(post.id);
    setComments(fresh);
  }

  async function react(emoji: string) {
    setReacted(emoji);
    try {
      await reactToPost(post.id, emoji);
    } catch {
      setReacted(null);
    }
  }

  return (
    <article className="rounded-lg border bg-card p-4" data-testid="livefeed-post">
      <div className="mb-2 flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--color-lop-bg-card-hi)] text-xs font-semibold uppercase text-[var(--color-lop-teal)]">
          {authorName.slice(0, 2)}
        </span>
        <span className="font-medium text-[var(--color-lop-text-primary)]">{authorName}</span>
        <span className="rounded-full bg-[var(--color-lop-bg-card-hi)] px-2 py-0.5 text-[10px] font-medium text-[var(--color-lop-text-secondary)]">
          {t(`livefeed.audience.${post.audience}`)}
        </span>
        <span className="ml-auto text-xs text-[var(--color-lop-text-muted)]">
          {timeAgo(post.postedAt, locale)}
        </span>
      </div>
      <p className="whitespace-pre-wrap text-sm text-[var(--color-lop-text-primary)]">{post.body}</p>

      <div className="mt-3 flex items-center gap-1">
        {REACTIONS.map((emoji) => (
          <button
            key={emoji}
            type="button"
            onClick={() => react(emoji)}
            className={`rounded-full px-2 py-0.5 text-sm transition-colors hover:bg-[var(--color-lop-bg-card-hi)] ${
              reacted === emoji ? 'bg-[var(--color-lop-bg-card-hi)] ring-1 ring-[var(--color-lop-teal)]' : ''
            }`}
            aria-label={`react ${emoji}`}
          >
            {emoji}
          </button>
        ))}
        <button
          type="button"
          onClick={toggleComments}
          className="ml-auto flex items-center gap-1 text-xs text-[var(--color-lop-text-secondary)] hover:text-[var(--color-lop-teal)]"
          data-testid="livefeed-toggle-comments"
        >
          <MessageSquare className="h-4 w-4" />
          {t('livefeed.comments.toggle')}
        </button>
      </div>

      {showComments && (
        <div className="mt-3 space-y-3 border-t border-[var(--color-lop-border)] pt-3">
          {loadingComments && (
            <p className="text-xs text-muted-foreground">{t('common.loading')}</p>
          )}
          {!loadingComments && comments.length === 0 && (
            <p className="text-xs text-muted-foreground">{t('livefeed.comments.empty')}</p>
          )}
          {comments.map((c) => (
            <div key={c.id} className="text-sm">
              <span className="font-medium text-[var(--color-lop-text-primary)]">
                {nameFor(c.authorUserId)}
              </span>{' '}
              <span className="text-[var(--color-lop-text-primary)]">{c.body}</span>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void submitComment();
              }}
              placeholder={t('livefeed.comments.placeholder')}
              className="flex h-9 flex-1 rounded-[6px] border border-[var(--color-lop-border)] bg-[var(--color-lop-bg-card)] px-3 text-sm text-[var(--color-lop-text-primary)] focus-visible:border-[var(--color-lop-teal)] focus-visible:outline-none"
              data-testid="livefeed-comment-input"
            />
            <Button size="sm" variant="outline" onClick={submitComment} disabled={!draft.trim()}>
              {t('livefeed.comments.send')}
            </Button>
          </div>
        </div>
      )}
    </article>
  );
}
